type Layer = {
  id: string;
  name: string;
  angle?: number;
  stroke?: string;
  strokeWidth?: number;
  left: number;
  top: number;
  width: number;
  height: number;
  opacity?: number;
  originX?: string;
  originY?: string;
  scaleX?: number;
  scaleY?: number;
  type: string;
  flipX?: boolean;
  flipY?: boolean;
  skewX?: number;
  skewY?: number;
  visible?: boolean;
  shadow?: any;
  preview?: string;
  src?: string;
  cropX?: number;
  cropY?: number;
  fill?: string;
  metadata?: any;
};

type Frame = {
  width: number;
  height: number;
};

type Design = {
  id: string;
  layers: Layer[];
  name?: string;
  frame?: Frame;
  preview?: string
};

type Template = {
  id: string;
  name: string;
  frame: Frame;
  scenes: Design[];
};

export function scaleLayouts(designs: Design[], newFrame: Frame): Design[] {
  return designs.map(design => {
    const currentFrame = design.frame;
    const scaleX = newFrame.width / currentFrame.width;
    const scaleY = newFrame.height / currentFrame.height;

    const scaledLayers = design.layers.map(layer => {
      // For Background layers, update width and height directly to match frame
      if (layer.type === 'Background') {
        return {
          ...layer,
          left: 0,
          top: 0,
          width: newFrame.width,
          height: newFrame.height,
          scaleX: 1,
          scaleY: 1,
        };
      }


      // For StaticText, use uniform scaling to avoid skewing
      if (layer.type === 'StaticText') {
        const uniformScale = Math.min(scaleX, scaleY);
        return {
          ...layer,
          left: layer.left * scaleX,
          top: layer.top * scaleY,
          scaleX: layer.scaleX ? layer.scaleX * uniformScale : 1,
          scaleY: layer.scaleY ? layer.scaleY * uniformScale : 1,
        };
      }

      // For other layers, scale position and scaleX/scaleY
      return {
        ...layer,
        left: layer.left * scaleX,
        top: layer.top * scaleY,
        scaleX: layer.scaleX ? layer.scaleX * scaleX : 1,
        scaleY: layer.scaleY ? layer.scaleY * scaleY : 1
      };
    });

    return {
      ...design,
      layers: scaledLayers,
      frame: newFrame,
    };
  });
}

export function scaleTemplate(template: Template, newFrame: Frame): Template {
  const currentTemplateFrame = template.frame;
  const scaleRatioX = newFrame.width / currentTemplateFrame.width;
  const scaleRatioY = newFrame.height / currentTemplateFrame.height;

  const scaledScenes = template.scenes.map(scene => {
    // Cover scenes are pre-computed at exact print pixels.
    // Skip scaling for:
    //  - scenes built dynamically (skipScaling flag from makeCoverScene)
    //  - scenes re-loaded from saved/downloaded projects (identified by name === 'Cover')
    if ((scene as any).skipScaling || scene.name === 'Cover') {
      return scene;
    }

    // Find this scene's original frame (defaults to template frame if not set)
    const sceneOriginalFrame = (scene as any).frame || currentTemplateFrame;

    // Scale this scene's frame proportionately by the template's overall scaling factors
    const sceneNewFrame = {
      width: sceneOriginalFrame.width * scaleRatioX,
      height: sceneOriginalFrame.height * scaleRatioY,
    };

    // Calculate layer scaling factor relative to the scene's own expansion
    const sceneScaleX = sceneNewFrame.width / sceneOriginalFrame.width;
    const sceneScaleY = sceneNewFrame.height / sceneOriginalFrame.height;

    const scaledLayers = scene.layers.map(layer => {
      // For Background layers, update width and height directly to match new scene frame
      if (layer.type === 'Background') {
        return {
          ...layer,
          left: 0,
          top: 0,
          width: sceneNewFrame.width,
          height: sceneNewFrame.height,
          scaleX: 1,
          scaleY: 1,
        };
      }

      // For StaticText, use uniform scaling to avoid skewing
      if (layer.type === 'StaticText') {
        const uniformScale = Math.min(sceneScaleX, sceneScaleY);
        return {
          ...layer,
          left: layer.left * sceneScaleX,
          top: layer.top * sceneScaleY,
          scaleX: layer.scaleX ? layer.scaleX * uniformScale : 1,
          scaleY: layer.scaleY ? layer.scaleY * uniformScale : 1,
        };
      }

      // For other layers, scale position and scaleX/scaleY based on scene scale
      return {
        ...layer,
        left: layer.left * sceneScaleX,
        top: layer.top * sceneScaleY,
        scaleX: layer.scaleX ? layer.scaleX * sceneScaleX : 1,
        scaleY: layer.scaleY ? layer.scaleY * sceneScaleY : 1,
      };
    });

    return {
      ...scene,
      layers: scaledLayers,
      frame: sceneNewFrame,
    };
  });

  return {
    ...template,
    scenes: scaledScenes,
    frame: newFrame,
  };
}